import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip } from 'recharts';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Info } from 'lucide-react';
import { useDashboard } from '@/contexts/DashboardContext';
import { formatTrendsXAxisLabel } from '@/lib/utils';
import { WidgetData } from '@/types';

type XAxisRangeType = 'hour' | 'day' | 'monthly' | 'year';

export interface CallStatusDistributionPoint {
  time: string;
  inboundMissed: number;
  inboundHandled: number;
  outboundMissed: number;
  outboundHandled: number;
  internalCall: number;
  inboundDirectHandled: number;
  inboundDirectMissed: number;
}

interface CallStatusDistributionTrendWidgetProps {
  data: WidgetData;
  loading?: boolean;
  error?: string;
  rangeType?: XAxisRangeType;
}

const getAreaColors = (theme: string) => ({
  inboundMissed: theme === 'dark' ? '#ef4444' : '#f87171',
  inboundHandled: theme === 'dark' ? '#60a5fa' : '#2563eb',
  outboundMissed: theme === 'dark' ? '#fbbf24' : '#fbbf24',
  outboundHandled: theme === 'dark' ? '#38bdf8' : '#22d3ee',
  internalCall: theme === 'dark' ? '#8E44AD' : '#8E44AD',
  inboundDirectHandled: theme === 'dark' ? '#10b981' : '#10b981',
  inboundDirectMissed: theme === 'dark' ? '#ec4899' : '#ec4899',
});

const areaNames = {
  inboundMissed: 'Inbound Missed',
  inboundHandled: 'Inbound Handled',
  outboundMissed: 'Outbound Missed',
  outboundHandled: 'Outbound Handled',
  internalCall: 'Internal Calls',
  inboundDirectHandled: 'Inbound Direct Handled',
  inboundDirectMissed: 'Inbound Direct Missed',
} as const;

type TooltipEntry = { payload: CallStatusDistributionPoint };

const ChartTooltip: React.FC<{ active?: boolean; payload?: TooltipEntry[]; label?: string; theme: string; rangeType: XAxisRangeType }> = ({ active, payload, label, theme, rangeType }) => {
  if (!active || !payload || !payload.length) return null;
  const colors = getAreaColors(theme);
  const row = payload[0]?.payload ?? {};
  return (
    <div className={`border rounded-lg shadow-md p-3 min-w-[220px] ${theme === 'dark' ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} style={{ fontSize: 14 }}>
      <div className="font-semibold">{formatTrendsXAxisLabel(label ?? '', rangeType)}</div>
      {Object.entries(areaNames).map(([key, name]) => (
        <div key={key} className="text-sm" style={{ color: colors[key as keyof typeof colors] }}>
          {name}: {row[key as keyof typeof row] ?? 0}
        </div>
      ))}
    </div>
  );
};

const CallStatusDistributionTrendWidget: React.FC<CallStatusDistributionTrendWidgetProps> = ({ data, loading, error, rangeType = 'day' }) => {
  const { theme } = useDashboard();
  const colors = getAreaColors(theme);

  const chartData = useMemo(() => {
    return data.value as CallStatusDistributionPoint[];
  }, [data?.value, rangeType]);

  const isAllZero = chartData?.length
    ? chartData.every((d: CallStatusDistributionPoint) =>
        (d.inboundMissed ?? 0) === 0 &&
        (d.inboundHandled ?? 0) === 0 &&
        (d.outboundMissed ?? 0) === 0 &&
        (d.outboundHandled ?? 0) === 0 &&
        (d.internalCall ?? 0) === 0 &&
        (d.inboundDirectHandled ?? 0) === 0 &&
        (d.inboundDirectMissed ?? 0) === 0,
      )
    : false;

  return (
    <div className="w-full min-w-[200px] min-h-[260px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#3C5C73] p-3">
      <div className="flex items-center gap-2">
        <div className="text-[14px] font-[600] text-primary dark:text-white truncate">Call Status Distribution Trend</div>
        <TooltipProvider>
          <UITooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="cursor-pointer">
                <Info className="h-4 w-4 text-primary dark:text-white" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="max-w-xs text-xs">The pattern of call status within the given time.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>

      {loading ? (
        <Skeleton className="w-full h-full rounded-none" />
      ) : error ? (
        <div className="text-red-500 text-center py-10">{error}</div>
      ) : chartData?.length === 0 ? (
        <div className="flex items-center justify-center h-full text-sm text-muted-foreground dark:text-white/60">
          <div className="flex flex-col items-center gap-2">
            <span>No call records were found</span>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ left: 0, right: 25, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              tickFormatter={(label) => formatTrendsXAxisLabel(String(label), rangeType)}
              tick={{ fontSize: 14, fill: theme === 'dark' ? '#fff' : '#6b7280' }}
              padding={{ left: 0, right: 16 }}
            />
            <YAxis
              tick={{ fontSize: 14, fill: theme === 'dark' ? '#fff' : '#6b7280' }}
              domain={isAllZero ? [0, 10] : undefined}
              ticks={isAllZero ? [0, 2, 4, 6, 8, 10] : undefined}
            />
            <Tooltip content={<ChartTooltip theme={theme} rangeType={rangeType} />} />
            <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: '14px', paddingBottom: '10px' }} />
            {!isAllZero && (
              <>
                <Line type="monotone" dataKey="inboundMissed" name={areaNames.inboundMissed} stroke={colors.inboundMissed} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundMissed, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.inboundMissed : colors.inboundMissed, stroke: colors.inboundMissed, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="inboundHandled" name={areaNames.inboundHandled} stroke={colors.inboundHandled} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundHandled, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.inboundHandled : colors.inboundHandled, stroke: colors.inboundHandled, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="outboundMissed" name={areaNames.outboundMissed} stroke={colors.outboundMissed} strokeWidth={2} dot={{ r: 4, stroke: colors.outboundMissed, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.outboundMissed : colors.outboundMissed, stroke: colors.outboundMissed, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="outboundHandled" name={areaNames.outboundHandled} stroke={colors.outboundHandled} strokeWidth={2} dot={{ r: 4, stroke: colors.outboundHandled, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.outboundHandled : colors.outboundHandled, stroke: colors.outboundHandled, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="internalCall" name={areaNames.internalCall} stroke={colors.internalCall} strokeWidth={2} dot={{ r: 4, stroke: colors.internalCall, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.internalCall : colors.internalCall, stroke: colors.internalCall, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="inboundDirectHandled" name={areaNames.inboundDirectHandled} stroke={colors.inboundDirectHandled} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundDirectHandled, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.inboundDirectHandled : colors.inboundDirectHandled, stroke: colors.inboundDirectHandled, strokeWidth: 2 }} />
                <Line type="monotone" dataKey="inboundDirectMissed" name={areaNames.inboundDirectMissed} stroke={colors.inboundDirectMissed} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundDirectMissed, strokeWidth: 2, fill: theme === 'dark' ? '#0f172a' : 'white' }} activeDot={{ r: 6, fill: theme === 'dark' ? colors.inboundDirectMissed : colors.inboundDirectMissed, stroke: colors.inboundDirectMissed, strokeWidth: 2 }} />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default CallStatusDistributionTrendWidget;


